/**
 * Quando o aviso de fim do PRO pode aparecer — irmão de `usePushPromptGate`
 * e `useLeagueWeekRecap`, com as MESMAS condições de "agora é hora de um
 * overlay" (decididas uma vez nesta casa; duplicar com outro critério é como
 * as duas buscas divergiram, BUG-81):
 *
 *  1. **Candidato** — `isPro === false` com `proExpiresAt` no passado
 *     (`isProLapseCandidate`, síncrono, zero read).
 *  2. **Ainda não visto** — chave por uid + valor de `proExpiresAt`.
 *  3. **Rota segura** (`isSafeCelebrationRoute`, importada — nunca uma
 *     allowlist própria).
 *  4. **Nenhum bloqueio** no `celebrationGate` (Foco, conclusão, Studio, o
 *     cartão de conquista, o convite de push…).
 *  5. **Assentamento de 1500ms** — os mesmos do convite de push, não os 600ms
 *     da celebração: isto é o app pedindo algo (voltar ao PRO), não
 *     respondendo a uma ação que a pessoa acabou de fazer.
 *
 * Só depois de tudo isso vem o único read (`subscriptions/{uid}`), para saber
 * a ORIGEM do PRO que acabou. Doc que não prova PRO de verdade (`incomplete`,
 * ausente) é MARCADO como visto e o aviso não abre — senão cada sessão pagaria
 * o read de novo para chegar à mesma conclusão.
 *
 * Claim por identidade (uid + proExpiresAt), nunca `cancelled`: a forma
 * booleana se destrói no `StrictMode` (BUG-141).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  isCelebrationBlocked,
  subscribeCelebrationGate,
} from "../../gamification/utils/celebrationGate";
import { isSafeCelebrationRoute } from "../../gamification/hooks/useBadgeUnlockQueue";
import { getOwnSubscriptionOnce } from "../../../services/firebase/subscriptionService";
import {
  hasSeenProLapse,
  isProLapseCandidate,
  markProLapseSeen,
  resolveProLapseOrigin,
  type ProLapseNotice,
} from "../utils/proLapse";

/** Tempo de a tela assentar antes de o app pedir alguma coisa. */
export const PRO_LAPSE_SETTLE_MS = 1500;

export interface ProLapseGateState {
  notice: ProLapseNotice | null;
  /** Fecha e registra como visto para esta conta/este lapso. */
  dismiss: () => void;
}

export function useProLapseGate(args: {
  uid: string | null | undefined;
  isPro: boolean | null | undefined;
  proExpiresAt: number | null | undefined;
}): ProLapseGateState {
  const { uid, isPro, proExpiresAt } = args;
  const { pathname } = useLocation();
  const [blocked, setBlocked] = useState<boolean>(() => isCelebrationBlocked());
  const [notice, setNotice] = useState<ProLapseNotice | null>(null);
  // Chave do pedido em voo/feito: `${uid}:${proExpiresAt}`. Identidade, não
  // booleano — ver o docblock.
  const requestedRef = useRef<string | null>(null);
  // `Date.now()` no corpo do render é impuro (regra do React Compiler).
  // Congelar na montagem é correto aqui: a pergunta é "já passou?", e o
  // `isPro === false` que a acompanha só muda por escrita do servidor.
  const [mountedAt] = useState(() => Date.now());

  useEffect(() => subscribeCelebrationGate(setBlocked), []);

  const candidate =
    !!uid &&
    typeof proExpiresAt === "number" &&
    isProLapseCandidate({ isPro, proExpiresAt, nowMs: mountedAt }) &&
    !hasSeenProLapse(uid, proExpiresAt);

  const canOpen = candidate && !notice && !blocked && isSafeCelebrationRoute(pathname);

  useEffect(() => {
    if (!canOpen || !uid || typeof proExpiresAt !== "number") return;
    const key = `${uid}:${proExpiresAt}`;
    if (requestedRef.current === key) return;

    const timer = setTimeout(() => {
      requestedRef.current = key;
      void (async () => {
        try {
          const sub = await getOwnSubscriptionOnce(uid);
          const origin = resolveProLapseOrigin(sub);
          if (!origin) {
            // Não houve PRO de verdade a lamentar. Marca como visto para esta
            // sessão e as próximas não pagarem o read pela mesma resposta.
            markProLapseSeen(uid, proExpiresAt);
            return;
          }
          setNotice({ origin, endedAt: proExpiresAt });
        } catch (err) {
          // Devolver o claim é o que permite tentar de novo na próxima rota
          // segura — falha de rede na abertura do app não pode esconder o
          // aviso até o reload.
          requestedRef.current = null;
          console.warn("[Lura] aviso de fim do PRO: leitura falhou", err);
        }
      })();
    }, PRO_LAPSE_SETTLE_MS);

    return () => clearTimeout(timer);
  }, [canOpen, uid, proExpiresAt]);

  const dismiss = useCallback(() => {
    if (uid && notice) markProLapseSeen(uid, notice.endedAt);
    setNotice(null);
  }, [uid, notice]);

  return { notice, dismiss };
}
